import React, { useEffect, useState } from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import MuiDialogContent from '@material-ui/core/DialogContent';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { Slide, Dialog, IconButton } from '@material-ui/core';
import { retentionImages } from '../../../constants/additionalImages';
import postMessage, { postMessageOfferFlow } from '../../../utils/postMessage';
import useSubscriptionFlowAnalyticsParams from '../../../common/hooks/useSubscriptionFlowAnalyticsParams';
import RetentionLogo from './retentionLogo';
import RetentionMessage from './retentionMessage';
import RetentionActions from './retentionActions';
import RetentionImageList from './retentionImageList';
import useProductData from '../../../common/hooks/useProductData';
import { useCodeTable } from '../../../common/hooks/useCodeTable';
import combinedHeroImage from '../../../images/retention/CombinedHeroImage.png';
import useApplyCoupon from '../../../common/hooks/useApplyCoupon';
import events from '../../../common/events/events';
import useUpdateSubscription from '../../../common/hooks/useUpdateSubscription';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  dialogModal: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
    },
    '& .MuiDialog-scrollBody .MuiDialog-paperScrollBody': {
      verticalAlign: 'top',
      maxWidth: '600px',
      margin: '20px',
      [theme.breakpoints.down('md')]: {
        left: '0px',
        width: '100%',
        marginLeft: '0px',
      },
    },
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundImage: `url(${combinedHeroImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '-2px 40px',
    backgroundColor: '#15151E',
    backgroundSize: '600px',
    [theme.breakpoints.down('md')]: {
      backgroundPosition: '0px 72px',
      backgroundSize: '100%',
    },
  },
  paperContainer: {
    height: 220,
    minWidth: '600px',
  },
  textContainer: {
    direction: 'row',
    justifyContent: 'center',
    marginTop: '50px',
  },
  imageList: {
    width: 500,
    height: 320,
  },
  topScrollBody: {
    alignItems: 'flex-start',
    width: '600px',
  },
  topScrollPaper: {
    alignItems: 'flex-start',
    width: '600px',
    backgroundColor: '#FFF',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(6),
    top: theme.spacing(3),
    color: theme.palette.custom.white,
    opacity: 1,
    paddingTop: '12px',
  },
  closeIcon: {
    fontSize: '28px',
    fontWeight: '700',
    lineHeight: 1,
    textShadow: `0 1px 0 ${theme.palette.custom.white}`,
    color: '#FFF',
  },
  closeButtonContent: {
    width: '30%',
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      boxShadow: `0 0 0 0 ${theme.palette.primary.dark} inset`,
      color: theme.palette.custom.lightGrey,
      background: theme.palette.primary.main,
    },
    [theme.breakpoints.between('xs', 'md')]: {
      width: '100%',
    },
  },
  closeButtonImage: {
    width: '24px',
    height: '24px',
  },
}));

const styles = (theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1.5),
    top: theme.spacing(0),
    color: theme.palette.custom.white,
    opacity: 0.3,
    paddingTop: '12px',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  button: {
    color: theme.palette.custom.white,
    borderRadius: '5px',
    margin: '12px 8px',
    fontWeight: 500,
    letterSpacing: 0.5,
    padding: '8px 12px',
    lineHeight: 1,
  },
  dialogTitle: {
    fontSize: '24px',
    lineHeight: 1.42,
    color: '#ffffff',
    [theme.breakpoints.between('xs', 'md')]: {
      fontSize: '22px',
    },
  },
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogModal = withStyles(styles)((props) => {
  const { children, classes, onClose, handleClose, open, ...other } = props;
  return (
    <Dialog
      onClose={handleClose}
      TransitionComponent={Transition}
      aria-labelledby="subscription-dialog-title"
      open={open}
      scroll="body"
      classes={{
        root: classes.root,
        paper: classes.paperContainer,
        scrollBody: classes.topScrollBody,
        scrollPaper: classes.topScrollPaper,
      }}
      {...other}
    >
      {children}
    </Dialog>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

export const RetentionOfferDialog = (props) => {
  const {
    open,
    grantCouponModel,
    isPostCancellationFlow,
    closeRetentionView,
    keepMySubscription,
    redeemedOffer,
    cancelledSubscription,
    showErrorDialogHandler,
    subscriptionProps,
    subId,
  } = props;

  const classes = useStyles();

  const hideLoader = () => {
    postMessage('module.hideLoader', {});
  };

  const showLoader = () => {
    postMessage('module.showLoader', {});
  };

  const showBackdrop = () => {
    postMessage('module.showBackdrop', {});
  };

  const hideBackdrop = () => {
    postMessage('module.hideBackdrop', {});
  };

  const [discountDetails, setDiscountDetails] = useState(undefined);
  const [terminationReasons, setTerminationReasons] = useState(undefined);

  const [applyCouponResponse, applyCouponFault, requestApplyCoupon] =
    useApplyCoupon();
  const [
    updateSubscription,
    updateSubscriptionFault,
    requestUpdateSubscription,
  ] = useUpdateSubscription();
  const [language, subscriptionType, subscriptionPackage] =
    useSubscriptionFlowAnalyticsParams();

  const [codeTable, codeTableFault, requestCodeTable] = useCodeTable();

  const [
    requestProductDataUiModel,
    requestProductDataFault,
    requestProductData,
  ] = useProductData();

  useEffect(() => {
    const retentionData = ASCENDON_ENV_CONFIG.retentionConfiguration;
    const productExternalReference = `${retentionData?.retentionProductExternalReference}`;
    const productExternalReferenceType = `${retentionData?.retentionProductExternalReferenceType}`;

    showLoader();
    requestProductData({
      productExternalReference,
      productExternalReferenceType,
    });

    if (!codeTable) {
      requestCodeTable([150]);
    }
    showBackdrop();
  }, []);

  useEffect(() => {
    setIframeHeight();
  });

  const setIframeHeight = () => {
    const elements = document.querySelectorAll('[data-iframe-height]');
    if (elements) {
      const padding = 50;
      window?.parentIFrame?.autoResize(false);
      window?.parentIFrame?.size(elements[0]?.offsetHeight + padding);
    }
  };

  useEffect(() => {
    if (codeTable) {
      setTerminationReasons(codeTable?.Codes);
    }
  }, [codeTable]);

  useEffect(() => {
    if (requestProductDataUiModel) {
      const pricingPlanId = grantCouponModel?.PricingPlanId;

      if (pricingPlanId) {
        const pricingPlan =
          requestProductDataUiModel?.metadata?.Product?.PricingPlans?.find(
            (candidate) => candidate?.Id === pricingPlanId,
          );
        if (pricingPlan) {
          const retentionDiscountDetailsText =
            pricingPlan?.AdditionalProperties?.find(
              (candidate) =>
                candidate?.ExternalReference === 'RetentionDiscountDetails',
            );
          if (
            retentionDiscountDetailsText &&
            retentionDiscountDetailsText?.Values.length > 0
          ) {
            const retentionDiscountDetails = JSON.parse(
              retentionDiscountDetailsText?.Values[0],
            );
            setDiscountDetails(retentionDiscountDetails);
          }
        }
      }
      if (hideLoader) {
        hideLoader();
      }

      setIframeHeight();
    }
  }, [requestProductDataUiModel]);

  useEffect(() => {
    if (requestProductDataFault) {
      if (hideLoader) {
        hideLoader();
      }
      showErrorDialogHandler(undefined, requestProductDataFault);
      setIframeHeight();
    }
  }, [requestProductDataFault]);

  const keepMySubscriptionAction = () => {
    postMessageOfferFlow('', {
      ...events.benefitsView.keepMySubscription,
      subscriptionPackage,
      subscriptionType,
      language,
    });
    if (keepMySubscription) {
      window?.parentIFrame?.autoResize(true);
      hideBackdrop();
      keepMySubscription();
    }
  };

  // Cancellation
  const continueCancellationAction = (terminationReason) => {
    window?.parentIFrame?.autoResize(true);
    cancelledSubscription(terminationReason);
  };

  // Redeem Offer
  const redeemOfferAction = () => {
    const handleRedeemCoupon = (isPostCancellationFlow) => {
      if (!isPostCancellationFlow) {
        postMessageOfferFlow('', {
          ...events.offerView.redeemOffer,
          subscriptionPackage,
          subscriptionType,
          language,
        });
      } else {
        postMessageOfferFlow('', {
          ...events.seeOffer.redeemOffer,
          subscriptionPackage,
          subscriptionType,
          language,
        });
      }
      applyCouponHandler();
    };
    handleRedeemCoupon(isPostCancellationFlow);
  };

  const applyCouponHandler = () => {
    if (!applyCouponResponse) {
      postMessage('module.showLoader');
      requestApplyCoupon({
        SubscriptionId: subId,
        RedemptionCodes: [grantCouponModel.RedemptionCode],
      });
    }
  };

  const renewSubscriptionAfterRedeem = () => {
    requestUpdateSubscription({
      Renew: true,
      Id: subId,
    });
  };

  useEffect(() => {
    if (applyCouponFault) {
      postMessage('module.hideLoader');
      window?.parentIFrame?.autoResize(true);
      showErrorDialogHandler(undefined, applyCouponFault);
    } else if (applyCouponResponse) {
      renewSubscriptionAfterRedeem();
    }
  }, [applyCouponResponse, applyCouponFault]);

  useEffect(() => {
    if (codeTable) {
      window?.parentIFrame?.autoResize(true);
      showErrorDialogHandler(undefined, updateSubscriptionFault);
      postMessage('module.hideLoader');
    }
  }, [updateSubscriptionFault]);

  useEffect(() => {
    if (updateSubscription && redeemedOffer) {
      window?.parentIFrame?.autoResize(true);
      postMessage('module.hideLoader');
      redeemedOffer({
        discountDetails,
        updateSubscriptionResponse: updateSubscription,
      });
    }
  }, [updateSubscription]);

  useEffect(() => {
    if (codeTable) {
      window?.parentIFrame?.autoResize(true);
      postMessage('module.hideLoader');
      showErrorDialogHandler(undefined, applyCouponFault);
    }
  }, [codeTableFault]);

  // Close Offer Dialog
  const closeAction = () => {
    window?.parentIFrame?.autoResize(true);
    hideBackdrop();

    postMessageOfferFlow('', {
      ...events.benefitsView.dismissed,
      subscriptionPackage,
      subscriptionType,
      language,
    });
    if (closeRetentionView) {
      closeRetentionView();
    }
  };

  const termsAndConditionLinkAction = () => {
    postMessage('module.termsAndConditionsButton', {});
  };

  return (
    <div>
      <DialogModal
        className={classes.dialogModal}
        handleClose={closeAction}
        open={open}
      >
        <DialogContent
          dividers
          className={classes.dialogContent}
          data-iframe-height
        >
          <div>
            <RetentionLogo></RetentionLogo>
            {closeAction && (
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                data-testid="retention-close-button"
                onClick={closeAction}
                disableRipple
                disableFocusRipple
                variant="text"
                size="small"
              >
                <CloseRoundedIcon className={classes.closeIcon} />
              </IconButton>
            )}
          </div>
          <RetentionMessage
            discountDetails={discountDetails}
          ></RetentionMessage>
          <RetentionImageList images={retentionImages()}></RetentionImageList>
          <RetentionActions
            redeemOfferAction={redeemOfferAction}
            keepMySubscriptionAction={keepMySubscriptionAction}
            closeAction={closeAction}
            continueCancellationAction={continueCancellationAction}
            termsAndConditionLinkAction={termsAndConditionLinkAction}
            terminationReasons={terminationReasons}
            discountDetails={discountDetails}
            subscriptionProps={subscriptionProps}
            isPostCancellationFlow={isPostCancellationFlow}
          ></RetentionActions>
        </DialogContent>
      </DialogModal>
    </div>
  );
};

export default RetentionOfferDialog;
